import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { Notification } from 'app/layout/common/notifications/notifications.types';
import type { Observable } from 'rxjs';
import { map, ReplaySubject, switchMap, take, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);

  private httpClient = inject(HttpClient);

  public readonly notifications$: Observable<Notification[]> = this.notifications.asObservable();

  /**
   * Get all notifications
   */
  public getAll(): Observable<Notification[]> {
    return this.httpClient.get<Notification[]>('api/common/notifications').pipe(
      tap((notifications) => {
        this.notifications.next(notifications);
      })
    );
  }

  /**
   * Create a notification
   *
   * @param notification
   */
  public create(notification: Notification): Observable<Notification> {
    return this.notifications$.pipe(
      take(1),
      switchMap((notifications) =>
        this.httpClient
          .post<Notification>('api/common/notifications', {
            notification,
          })
          .pipe(
            map((newNotification) => {
              // Update the notifications with the new notification
              this.notifications.next([...notifications, newNotification]);

              // Return the new notification from observable
              return newNotification;
            })
          )
      )
    );
  }

  /**
   * Update the notification
   *
   * @param id
   * @param notification
   */
  public update(id: string, notification: Notification): Observable<Notification> {
    return this.notifications$.pipe(
      take(1),
      switchMap((notifications) =>
        this.httpClient
          .patch<Notification>('api/common/notifications', {
            id,
            notification,
          })
          .pipe(
            map((updatedNotification: Notification) => {
              // Find the index of the updated notification
              const index = notifications.findIndex((item) => item.id === id);

              // Update the notification
              notifications[index] = updatedNotification;

              // Update the notifications
              this.notifications.next(notifications);

              // Return the updated notification
              return updatedNotification;
            })
          )
      )
    );
  }

  /**
   * Delete the notification
   *
   * @param id
   */
  public delete(id: string): Observable<boolean> {
    return this.notifications$.pipe(
      take(1),
      switchMap((notifications) =>
        this.httpClient
          .delete<boolean>('api/common/notifications', {
            params: { id },
          })
          .pipe(
            map((isDeleted: boolean) => {
              // Find the index of the deleted notification
              const index = notifications.findIndex((item) => item.id === id);

              // Delete the notification
              notifications.splice(index, 1);

              // Update the notifications
              this.notifications.next(notifications);

              // Return the deleted status
              return isDeleted;
            })
          )
      )
    );
  }

  /**
   * Mark all notifications as read
   */
  public markAllAsRead(): Observable<boolean> {
    return this.notifications$.pipe(
      take(1),
      switchMap((notifications) =>
        this.httpClient.get<boolean>('api/common/notifications/mark-all-as-read').pipe(
          map((isUpdated: boolean) => {
            // Go through all notifications and set them as read
            notifications.forEach((notification, index) => {
              notifications[index].read = true;
            });

            // Update the notifications
            this.notifications.next(notifications);

            // Return the updated status
            return isUpdated;
          })
        )
      )
    );
  }
}
